import { useMemo } from 'react';
import { formatCurrency } from '../../../../utils/collateralFormatters';
import type { Claim, ClaimCharges } from '../../../../types/LegalCase';

interface ClaimCompositionBarProps {
    claim: Claim;
}

interface Segment {
    key: string;
    label: string;
    value: number;
    formatted: string;
    color: string;
}

interface Composition {
    segments: Segment[];
    total: number;
    totalFormatted: string;
    title: string;
}

// Os *Formatted vêm do backend, mas o tipo permite null; fecha o buraco derivando do número
// (que sempre existe) quando o texto formatado não veio, para nunca renderizar "null" na tela.
const money = (formatted: string | null, value: number): string => formatted ?? formatCurrency(value);

// Paleta fixa por componente da pretensão (não depende de ordem). Cores neutras + o azul/verde
// da identidade Agrolend, coerentes com o resto do backoffice.
const COMPONENT_COLORS: Record<string, string> = {
    principal: '#3B82F6',   // azul — ação/principal
    interest: '#F59E0B',    // âmbar — juros
    penalties: '#EF4444',   // vermelho — multa
    legalCosts: '#8B5CF6',  // roxo — custas
    items: '#10B981',       // verde — títulos
};

const positive = (segments: Segment[]): Segment[] => segments.filter((s) => s.value > 0);

// Composição do **valor atualizado** — a decomposição de quanto a dívida vale hoje: saldo em
// aberto + os juros e a multa que as taxas do caso produziram + custas. É a mesma base do card
// "Saldo a recuperar" do Resumo (`caseResult.ts`), de propósito: duas telas mostrando números de
// bases diferentes é o que confundiu na validação da AF-3816.
//
// A fatia do principal é o `outstandingAmount` (já descontado o pago), não a base cheia — senão a
// soma das fatias não fecharia com o total. O rótulo diz "Títulos" quando o principal vem deles
// (ADR 005, decisão 6: com títulos, o principal **é** a soma deles, não uma fatia à parte).
const compositionFromCharges = (claim: Claim, charges: ClaimCharges): Composition => {
    const fromItems = !!claim.principalFromItems;
    const candidates: Segment[] = [
        {
            key: 'principal',
            label: fromItems ? 'Títulos em aberto' : 'Principal em aberto',
            value: charges.outstandingAmount || 0,
            formatted: money(charges.outstandingAmountFormatted, charges.outstandingAmount || 0),
            color: fromItems ? COMPONENT_COLORS.items : COMPONENT_COLORS.principal,
        },
        { key: 'interest', label: 'Juros', value: charges.interestAmount || 0, formatted: money(charges.interestAmountFormatted, charges.interestAmount || 0), color: COMPONENT_COLORS.interest },
        { key: 'penalties', label: 'Multa', value: charges.penaltyAmount || 0, formatted: money(charges.penaltyAmountFormatted, charges.penaltyAmount || 0), color: COMPONENT_COLORS.penalties },
        { key: 'legalCosts', label: 'Custas', value: charges.legalCosts || 0, formatted: money(charges.legalCostsFormatted, charges.legalCosts || 0), color: COMPONENT_COLORS.legalCosts },
    ];
    return {
        segments: positive(candidates),
        total: charges.updatedAmount || 0,
        totalFormatted: money(charges.updatedAmountFormatted, charges.updatedAmount || 0),
        title: 'Composição do valor atualizado',
    };
};

// Fallback: sem `charges` (nenhuma taxa cadastrada, ou resposta anterior à Task 17) decompõe a
// pretensão **como registrada** — os valores lançados à mão. Melhor esta barra que nenhuma.
const compositionFromClaim = (claim: Claim): Composition => {
    const fromItems = !!claim.principalFromItems;
    const candidates: Segment[] = [
        {
            key: 'principal',
            label: fromItems ? 'Títulos' : 'Principal',
            value: claim.principal || 0,
            formatted: money(claim.principalFormatted, claim.principal || 0),
            color: fromItems ? COMPONENT_COLORS.items : COMPONENT_COLORS.principal,
        },
        { key: 'interest', label: 'Juros', value: claim.interest || 0, formatted: money(claim.interestFormatted, claim.interest || 0), color: COMPONENT_COLORS.interest },
        { key: 'penalties', label: 'Multa', value: claim.penalties || 0, formatted: money(claim.penaltiesFormatted, claim.penalties || 0), color: COMPONENT_COLORS.penalties },
        { key: 'legalCosts', label: 'Custas', value: claim.legalCosts || 0, formatted: money(claim.legalCostsFormatted, claim.legalCosts || 0), color: COMPONENT_COLORS.legalCosts },
    ];
    return {
        segments: positive(candidates),
        total: claim.grandTotal || 0,
        totalFormatted: money(claim.grandTotalFormatted, claim.grandTotal || 0),
        title: 'Composição da pretensão',
    };
};

const buildComposition = (claim: Claim): Composition =>
    (claim.charges ? compositionFromCharges(claim, claim.charges) : compositionFromClaim(claim));

const pct = (value: number, total: number): number => (total > 0 ? (value / total) * 100 : 0);

// Mesma regra do card do Resumo (`recoverableFromClaim` em `caseResult.ts`): o que ainda se tem a
// receber é o valor atualizado; sem `charges`, cai na pretensão registrada menos o pago.
const remainingOf = (claim: Claim): number => claim.charges?.updatedAmount ?? (claim.remainingTotal || 0);

const remainingTextOf = (claim: Claim): string => {
    const charges = claim.charges;
    if (charges) return money(charges.updatedAmountFormatted, charges.updatedAmount || 0);
    return money(claim.remainingTotalFormatted, claim.remainingTotal || 0);
};

// Barra de composição da pretensão: uma barra horizontal segmentada (principal/juros/multa/
// custas/títulos) + barra pago/saldo quando há pagamento. SVG/divs puro, sem lib de gráfico
// (padrão do PieChart do CCIR). Garantia NÃO entra — fica fora do total (ADR 002); é reportada
// à parte na seção. Só renderiza o que tem valor, para não poluir casos simples.
const ClaimCompositionBar = ({ claim }: ClaimCompositionBarProps) => {
    const composition = useMemo(() => buildComposition(claim), [claim]);
    const { segments, total } = composition;

    // Sem valores compostos (só título, ou pretensão zerada) não há o que decompor.
    if (segments.length <= 1 && claim.paidTotal <= 0) return null;

    const hasPayment = claim.paidTotal > 0;
    // Pago × a receber: o denominador é **pago + a receber**, não a pretensão registrada. Com
    // encargos correndo, a dívida cresce depois do ajuizamento — usar o valor registrado como
    // denominador mostraria "90% pago" num caso onde o saldo com juros ainda é maior que o pago.
    const remaining = remainingOf(claim);
    const paidPct = Math.min(100, pct(claim.paidTotal, claim.paidTotal + remaining));
    const remainingPct = Math.max(0, 100 - paidPct);
    const paidText = money(claim.paidTotalFormatted, claim.paidTotal);
    const remainingText = remainingTextOf(claim);

    return (
        <div className="space-y-3">
            {segments.length > 1 && (
                <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>{composition.title}</span>
                        <span className="font-medium text-gray-900 dark:text-white">{composition.totalFormatted}</span>
                    </div>
                    <div
                        className="flex h-3 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-slate-700"
                        role="img"
                        aria-label={`${composition.title}: ${segments.map((s) => `${s.label} ${s.formatted}`).join(', ')}`}
                    >
                        {segments.map((s) => (
                            <div
                                key={s.key}
                                style={{ width: `${pct(s.value, total)}%`, backgroundColor: s.color }}
                                title={`${s.label}: ${s.formatted}`}
                            />
                        ))}
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1">
                        {segments.map((s) => (
                            <div key={s.key} className="flex items-center gap-1.5 text-xs">
                                <span className="inline-block h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                                <span className="text-gray-600 dark:text-gray-300">{s.label}</span>
                                <span className="text-gray-400 dark:text-gray-500">{s.formatted}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {hasPayment && (
                <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>Pago vs. saldo</span>
                        <span className="font-medium text-gray-900 dark:text-white">{remainingText} em aberto</span>
                    </div>
                    <div
                        className="flex h-3 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-slate-700"
                        role="img"
                        aria-label={`Pago ${paidText}, saldo remanescente ${remainingText}`}
                    >
                        <div style={{ width: `${paidPct}%` }} className="bg-green-500" title={`Pago: ${paidText}`} />
                        <div style={{ width: `${remainingPct}%` }} className="bg-gray-300 dark:bg-slate-500" title={`Saldo: ${remainingText}`} />
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
                        <div className="flex items-center gap-1.5">
                            <span className="inline-block h-2.5 w-2.5 rounded-full bg-green-500 shrink-0" />
                            <span className="text-gray-600 dark:text-gray-300">Pago</span>
                            <span className="text-gray-400 dark:text-gray-500">{paidText}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <span className="inline-block h-2.5 w-2.5 rounded-full bg-gray-300 dark:bg-slate-500 shrink-0" />
                            <span className="text-gray-600 dark:text-gray-300">Saldo</span>
                            <span className="text-gray-400 dark:text-gray-500">{remainingText}</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClaimCompositionBar;
